// Bundled copy of .agents/skills/mighty45-org-api/SKILL.md — keep in sync when editing the skill.
import orgApiSkillTemplate from "./mighty45-org-api-SKILL.md?raw";

export const MIGHTY45_API_BASE_URL = "https://app.mighty45.com/api/v1";

export { orgApiSkillTemplate };

/**
 * Full Cursor/agent SKILL.md with org-specific placeholders filled in when available.
 * @param {{ organizationId?: string, apiKey?: string }} params
 */
export function buildOrgApiSkillSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
} = {}) {
  return orgApiSkillTemplate
    .replaceAll("YOUR_ORGANIZATION_ID", organizationId)
    .replaceAll("YOUR_API_KEY", apiKey);
}

export const API_SAMPLE_DOWNLOADS = [
  {
    label: "curl (add-client.sh)",
    href: "/api-docs/samples/add-client.sh",
  },
  {
    label: "Node.js (add-client.js)",
    href: "/api-docs/samples/add-client.js",
  },
  {
    label: "Python (add-client.py)",
    href: "/api-docs/samples/add-client.py",
  },
  {
    label: "Full API reference (markdown)",
    href: "/api-docs/mighty45-organization-api.md",
  },
  {
    label: "AI agent skill (SKILL.md)",
    href: "/api-docs/mighty45-org-api-SKILL.md",
  },
];

/**
 * @param {{ organizationId?: string, apiKey?: string, coachId?: string }} params
 */
export function buildAddClientCurlSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
  coachId = "COACH_USER_UID",
} = {}) {
  return `curl -sS -X POST "${MIGHTY45_API_BASE_URL}/add_client" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "organizationid: ${organizationId}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "firstName": "Alex",
    "lastName": "Rivera",
    "coachId": "${coachId}",
    "email": "alex@example.com"
  }'`;
}

/**
 * @param {{ organizationId?: string, apiKey?: string, coachId?: string }} params
 */
export function buildAddClientNodeSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
  coachId = "COACH_USER_UID",
} = {}) {
  return `const response = await fetch("${MIGHTY45_API_BASE_URL}/add_client", {
  method: "POST",
  headers: {
    Authorization: "Bearer ${apiKey}",
    organizationid: "${organizationId}",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    firstName: "Alex",
    lastName: "Rivera",
    coachId: "${coachId}",
    email: "alex@example.com",
  }),
});

if (!response.ok) {
  throw new Error(await response.text());
}

console.log(await response.json());`;
}

/**
 * @param {{ organizationId?: string, apiKey?: string, coachId?: string }} params
 */
export function buildAddClientPythonSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
  coachId = "COACH_USER_UID",
} = {}) {
  return `import requests

response = requests.post(
    "${MIGHTY45_API_BASE_URL}/add_client",
    headers={
        "Authorization": "Bearer ${apiKey}",
        "organizationid": "${organizationId}",
        "Content-Type": "application/json",
    },
    json={
        "firstName": "Alex",
        "lastName": "Rivera",
        "coachId": "${coachId}",
        "email": "alex@example.com",
    },
    timeout=30,
)
response.raise_for_status()
print(response.json())`;
}

export const ADD_CLIENT_REQUEST_EXAMPLE = `{
  "firstName": "Alex",
  "lastName": "Rivera",
  "coachId": "COACH_USER_UID",
  "email": "alex@example.com",
  "experience": "intermediate",
  "goal": "Build strength",
  "nutritionGoals": {
    "name": "Default",
    "proteins": 150,
    "carbs": 200,
    "fats": 65,
    "water": 80
  }
}`;

export const ADD_CLIENT_SUCCESS_EXAMPLE = `{
  "status": "OK",
  "uid": "NEW_CLIENT_UID"
}`;

/**
 * @param {{ organizationId?: string, apiKey?: string, query?: string }} params
 */
export function buildClientSearchCurlSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
  query = "alex",
} = {}) {
  return `curl -sS "${MIGHTY45_API_BASE_URL}/org/clients?q=${encodeURIComponent(query)}&limit=10" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "organizationid: ${organizationId}"`;
}

/**
 * @param {{ organizationId?: string, apiKey?: string, clientId?: string }} params
 */
export function buildClientSummaryCurlSample({
  organizationId = "YOUR_ORGANIZATION_ID",
  apiKey = "YOUR_API_KEY",
  clientId = "CLIENT_UID",
  detail = "full",
} = {}) {
  return `curl -sS "${MIGHTY45_API_BASE_URL}/org/clients/${clientId}/summary?detail=${detail}&healthDays=90&exerciseLimit=30" \\
  -H "Authorization: Bearer ${apiKey}" \\
  -H "organizationid: ${organizationId}"`;
}

export const CLIENT_SEARCH_RESPONSE_EXAMPLE = `{
  "success": true,
  "data": {
    "query": "alex",
    "count": 1,
    "clients": [
      {
        "clientId": "CLIENT_UID",
        "firstName": "Alex",
        "lastName": "Rivera",
        "email": "alex@example.com",
        "status": "active"
      }
    ]
  }
}`;

export const CLIENT_SUMMARY_SECTIONS = [
  "identity",
  "profile",
  "training",
  "nutrition",
  "analytics",
  "recentActivity (summary or full)",
  "coaching",
  "extended.weeklyReports",
  "extended.progressPhotos",
  "extended.coachLogs",
  "extended.progressTrends",
];

export const CLIENT_SUMMARY_DETAIL_LEVELS = {
  summary:
    "Default. Aggregated health macros, workout completion counts, weekly report metadata.",
  full:
    "Full Firestore payloads: health optional/micronutrients, exercise sets/reps/weights/notes, supplement history, current workout, embedded weekly report data, strength-by-exercise trends.",
};
